import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Users,
  TrendingUp,
  DollarSign,
  Settings,
  Shield,
  AlertTriangle,
  Zap,
  Target,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultState = {
  companyName: "",
  industry: "",

  offering: "",
  targetCustomer: "",
  avgDealSize: 1000,
  differentiation: "some",
  topCompetitor: "",

  leadChannels: ["referrals"],
  monthlyLeads: 20,
  closeRatePct: 10,
  cac: 0,

  monthlyRevenue: 10000,
  grossMarginPct: 50,
  repeatCustomerPct: 20,
  recurringModel: "some",

  hasCRM: false,
  hasAccounting: false,
  hasMarketingAutomation: false,
  opsToolingLevel: "mixed",

  regulated: false,
  regulatedAreas: [],
  handlesPII: true,
  crossBorder: false,

  biggestBottleneck: "demand",
  headcount: 1,

  whatBreaksAt2x: "Fulfillment and response time",

  expansionIdeas: "Add a second offer tier and expand into a nearby vertical",

  monthlyBudget: 2000,
  urgency: "normal",

  goal: "dominate_market",
  goalHorizonMonths: 36,
};

const stepInfo = [
  { num: 1, title: "Company", icon: Building2 },
  { num: 2, title: "Offering & Customer", icon: Users },
  { num: 3, title: "Lead Sources", icon: TrendingUp },
  { num: 4, title: "Revenue & Margins", icon: DollarSign },
  { num: 5, title: "Systems", icon: Settings },
  { num: 6, title: "Compliance", icon: Shield },
  { num: 7, title: "Bottleneck", icon: AlertTriangle },
  { num: 8, title: "Scale Stress", icon: Zap },
  { num: 9, title: "Expansion & Budget", icon: TrendingUp },
  { num: 10, title: "Goal", icon: Target },
];

const leadChannelOptions = [
  { value: "referrals", label: "Referrals" },
  { value: "google", label: "Google Ads" },
  { value: "meta_ads", label: "Meta/Facebook Ads" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "cold_email", label: "Cold Email" },
  { value: "partners", label: "Partners" },
  { value: "events", label: "Events" },
  { value: "seo", label: "SEO/Organic" },
  { value: "content", label: "Content Marketing" },
];

const regulatedAreasOptions = [
  { value: "payments", label: "Payments" },
  { value: "shipping", label: "Shipping" },
  { value: "health", label: "Health/Medical" },
  { value: "finance", label: "Finance" },
  { value: "money_services", label: "Money Services" },
  { value: "crypto", label: "Crypto" },
  { value: "food", label: "Food/Beverage" },
  { value: "real_estate", label: "Real Estate" },
];

const CatalystWizard = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(defaultState);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const totalSteps = 10;

  const toggleInList = (list, val) => {
    return list.includes(val) ? list.filter(x => x !== val) : [...list, val];
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/catalyst/run`, {
        ...formData,
        leadChannels: Array.isArray(formData.leadChannels) ? formData.leadChannels : [],
        regulatedAreas: Array.isArray(formData.regulatedAreas) ? formData.regulatedAreas : [],
        cac: formData.cac && Number(formData.cac) > 0 ? Number(formData.cac) : undefined,
      });

      if (response.data.ok) {
        toast.success('Catalyst analysis complete!');
        navigate(`/catalyst/results/${response.data.runId}`);
      } else {
        throw new Error(response.data.error || 'Failed to run Catalyst');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to run Catalyst');
      toast.error('Failed to generate program');
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    setError(null);
    setStep(s => Math.min(totalSteps, s + 1));
  };

  const prevStep = () => {
    setError(null);
    setStep(s => Math.max(1, s - 1));
  };

  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/catalyst')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold">Catalyst Wizard</h1>
              <p className="text-blue-200 text-sm">Step {step} of {totalSteps}</p>
            </div>
          </div>
          <Badge className="bg-amber-500 text-white">
            {Math.round(progress)}% Complete
          </Badge>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-blue-800 h-2">
        <div 
          className="bg-amber-500 h-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Step Indicators */}
      <div className="bg-white border-b py-4 px-6 overflow-x-auto">
        <div className="max-w-4xl mx-auto flex gap-2 min-w-max">
          {stepInfo.map((s) => {
            const Icon = s.icon;
            const isActive = step === s.num;
            const isComplete = step > s.num;
            
            return (
              <button
                key={s.num}
                onClick={() => setStep(s.num)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-blue-900 text-white'
                    : isComplete
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {isComplete ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{s.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-2xl mx-auto p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <Card className="border border-gray-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {React.createElement(stepInfo[step - 1].icon, { className: "w-5 h-5 text-blue-900" })}
              {stepInfo[step - 1].title}
            </CardTitle>
            <CardDescription>
              {step === 1 && "Tell us about your company"}
              {step === 2 && "Describe what you offer and who you serve"}
              {step === 3 && "How do you generate leads?"}
              {step === 4 && "Your revenue and margin profile"}
              {step === 5 && "What systems do you have in place?"}
              {step === 6 && "Compliance and regulatory considerations"}
              {step === 7 && "What's holding you back?"}
              {step === 8 && "What breaks when you double?"}
              {step === 9 && "Growth plans and budget"}
              {step === 10 && "Your ultimate goal"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Company */}
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={formData.companyName}
                    onChange={e => updateField('companyName', e.target.value)}
                    placeholder="e.g., Acme Corp"
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Industry *</Label>
                  <Input
                    value={formData.industry}
                    onChange={e => updateField('industry', e.target.value)}
                    placeholder="e.g., E-commerce, SaaS, Professional Services"
                    data-testid="industry-input"
                  />
                </div>
              </>
            )}

            {/* Step 2: Offering & Customer */}
            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label>What do you offer? *</Label>
                  <Textarea
                    value={formData.offering}
                    onChange={e => updateField('offering', e.target.value)}
                    placeholder="Describe your main product or service..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Who is your target customer? *</Label>
                  <Input
                    value={formData.targetCustomer}
                    onChange={e => updateField('targetCustomer', e.target.value)}
                    placeholder="e.g., Small business owners in retail"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Average Deal Size ($)</Label>
                  <Input
                    type="number"
                    value={formData.avgDealSize}
                    onChange={e => updateField('avgDealSize', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>How differentiated is your offering?</Label>
                  <Select value={formData.differentiation} onValueChange={v => updateField('differentiation', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None - We compete on price</SelectItem>
                      <SelectItem value="some">Some - We have a few unique features</SelectItem>
                      <SelectItem value="strong">Strong - Clear differentiation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Top Competitor (optional)</Label>
                  <Input
                    value={formData.topCompetitor}
                    onChange={e => updateField('topCompetitor', e.target.value)}
                    placeholder="e.g., Competitor Inc"
                  />
                </div>
              </>
            )}

            {/* Step 3: Lead Sources */}
            {step === 3 && (
              <>
                <div className="space-y-3">
                  <Label>Lead Channels (select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {leadChannelOptions.map(opt => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all ${
                          formData.leadChannels.includes(opt.value)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Checkbox
                          checked={formData.leadChannels.includes(opt.value)}
                          onCheckedChange={() => updateField('leadChannels', toggleInList(formData.leadChannels, opt.value))}
                        />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Monthly Leads</Label>
                    <Input
                      type="number"
                      value={formData.monthlyLeads}
                      onChange={e => updateField('monthlyLeads', Number(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Close Rate (%)</Label>
                    <Input
                      type="number"
                      value={formData.closeRatePct}
                      onChange={e => updateField('closeRatePct', Number(e.target.value))}
                      min={0}
                      max={100}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Customer Acquisition Cost ($, optional)</Label>
                  <Input
                    type="number"
                    value={formData.cac}
                    onChange={e => updateField('cac', Number(e.target.value))}
                    placeholder="Leave 0 if unknown"
                  />
                </div>
              </>
            )}

            {/* Step 4: Revenue & Margins */}
            {step === 4 && (
              <>
                <div className="space-y-2">
                  <Label>Monthly Revenue ($)</Label>
                  <Input
                    type="number"
                    value={formData.monthlyRevenue}
                    onChange={e => updateField('monthlyRevenue', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gross Margin (%)</Label>
                  <Input
                    type="number"
                    value={formData.grossMarginPct}
                    onChange={e => updateField('grossMarginPct', Number(e.target.value))}
                    min={0}
                    max={100}
                  />
                  <p className="text-xs text-gray-500">Revenue minus direct costs (COGS)</p>
                </div>
                <div className="space-y-2">
                  <Label>Repeat Customer Rate (%)</Label>
                  <Input
                    type="number"
                    value={formData.repeatCustomerPct}
                    onChange={e => updateField('repeatCustomerPct', Number(e.target.value))}
                    min={0}
                    max={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Recurring Revenue Model</Label>
                  <Select value={formData.recurringModel} onValueChange={v => updateField('recurringModel', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None - All one-time</SelectItem>
                      <SelectItem value="some">Some - Partial recurring</SelectItem>
                      <SelectItem value="mostly">Mostly - Subscription-based</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Step 5: Systems */}
            {step === 5 && (
              <>
                <div className="space-y-4">
                  <Label>Which systems do you have?</Label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={formData.hasCRM}
                        onCheckedChange={v => updateField('hasCRM', v)}
                      />
                      <div>
                        <span className="font-medium">CRM System</span>
                        <p className="text-sm text-gray-500">HubSpot, Salesforce, Pipedrive, etc.</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={formData.hasAccounting}
                        onCheckedChange={v => updateField('hasAccounting', v)}
                      />
                      <div>
                        <span className="font-medium">Accounting Software</span>
                        <p className="text-sm text-gray-500">QuickBooks, Xero, FreshBooks, etc.</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={formData.hasMarketingAutomation}
                        onCheckedChange={v => updateField('hasMarketingAutomation', v)}
                      />
                      <div>
                        <span className="font-medium">Marketing Automation</span>
                        <p className="text-sm text-gray-500">Mailchimp, ActiveCampaign, Klaviyo, etc.</p>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Operations Tooling Level</Label>
                  <Select value={formData.opsToolingLevel} onValueChange={v => updateField('opsToolingLevel', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual - Spreadsheets & email</SelectItem>
                      <SelectItem value="mixed">Mixed - Some tools, some manual</SelectItem>
                      <SelectItem value="systemized">Systemized - Integrated stack</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Step 6: Compliance */}
            {step === 6 && (
              <>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={formData.regulated}
                        onCheckedChange={v => updateField('regulated', v)}
                      />
                      <div>
                        <span className="font-medium">Regulated Business</span>
                        <p className="text-sm text-gray-500">Subject to industry-specific regulations</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={formData.handlesPII}
                        onCheckedChange={v => updateField('handlesPII', v)}
                      />
                      <div>
                        <span className="font-medium">Handles Personal Data (PII)</span>
                        <p className="text-sm text-gray-500">Customer names, emails, payment info, etc.</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={formData.crossBorder}
                        onCheckedChange={v => updateField('crossBorder', v)}
                      />
                      <div>
                        <span className="font-medium">Cross-Border Operations</span>
                        <p className="text-sm text-gray-500">International customers or operations</p>
                      </div>
                    </label>
                  </div>
                </div>
                {formData.regulated && (
                  <div className="space-y-3">
                    <Label>Regulated Areas (select applicable)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {regulatedAreasOptions.map(opt => (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all ${
                            formData.regulatedAreas.includes(opt.value)
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Checkbox
                            checked={formData.regulatedAreas.includes(opt.value)}
                            onCheckedChange={() => updateField('regulatedAreas', toggleInList(formData.regulatedAreas, opt.value))}
                          />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Step 7: Bottleneck */}
            {step === 7 && (
              <>
                <div className="space-y-2">
                  <Label>What's your biggest bottleneck right now?</Label>
                  <Select value={formData.biggestBottleneck} onValueChange={v => updateField('biggestBottleneck', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="demand">Demand - Need more leads/customers</SelectItem>
                      <SelectItem value="capacity">Capacity - Can't fulfill more orders</SelectItem>
                      <SelectItem value="cash">Cash - Running low on capital</SelectItem>
                      <SelectItem value="people">People - Need to hire</SelectItem>
                      <SelectItem value="systems">Systems - Processes are broken</SelectItem>
                      <SelectItem value="compliance">Compliance - Regulatory constraints</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Current Headcount</Label>
                  <Input
                    type="number"
                    value={formData.headcount}
                    onChange={e => updateField('headcount', Number(e.target.value))}
                    min={0}
                  />
                  <p className="text-xs text-gray-500">Including yourself and any contractors</p>
                </div>
              </>
            )}

            {/* Step 8: Scale Stress */}
            {step === 8 && (
              <>
                <div className="space-y-2">
                  <Label>What breaks if you double your revenue tomorrow?</Label>
                  <Textarea
                    value={formData.whatBreaksAt2x}
                    onChange={e => updateField('whatBreaksAt2x', e.target.value)}
                    placeholder="e.g., We couldn't fulfill orders fast enough, customer support would be overwhelmed..."
                    rows={4}
                  />
                  <p className="text-xs text-gray-500">Be specific about which processes, roles, or systems would fail</p>
                </div>
              </>
            )}

            {/* Step 9: Expansion & Budget */}
            {step === 9 && (
              <>
                <div className="space-y-2">
                  <Label>Expansion Ideas</Label>
                  <Textarea
                    value={formData.expansionIdeas}
                    onChange={e => updateField('expansionIdeas', e.target.value)}
                    placeholder="e.g., New product line, new market, franchise, geographic expansion..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Budget for Growth ($)</Label>
                  <Input
                    type="number"
                    value={formData.monthlyBudget}
                    onChange={e => updateField('monthlyBudget', Number(e.target.value))}
                  />
                  <p className="text-xs text-gray-500">How much can you invest monthly in marketing, systems, and hiring?</p>
                </div>
                <div className="space-y-2">
                  <Label>Urgency</Label>
                  <Select value={formData.urgency} onValueChange={v => updateField('urgency', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fast">Fast - Need results ASAP</SelectItem>
                      <SelectItem value="normal">Normal - Steady progress</SelectItem>
                      <SelectItem value="slow">Slow - Long-term focus</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Step 10: Goal */}
            {step === 10 && (
              <>
                <div className="space-y-2">
                  <Label>What's your end goal?</Label>
                  <Select value={formData.goal} onValueChange={v => updateField('goal', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exit">Exit - Sell the business</SelectItem>
                      <SelectItem value="franchise">Franchise - Replicate the model</SelectItem>
                      <SelectItem value="lifestyle">Lifestyle - Profitable & sustainable</SelectItem>
                      <SelectItem value="raise_capital">Raise Capital - Get investors</SelectItem>
                      <SelectItem value="dominate_market">Dominate Market - Be the leader</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Goal Horizon (months)</Label>
                  <Input
                    type="number"
                    value={formData.goalHorizonMonths}
                    onChange={e => updateField('goalHorizonMonths', Number(e.target.value))}
                    min={1}
                    max={240}
                  />
                  <p className="text-xs text-gray-500">When do you want to achieve this goal?</p>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t">
                  <Button
                    className="w-full bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold py-6 text-lg"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    data-testid="catalyst-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating Your Program...
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5 mr-2" />
                        Generate Catalyst Program
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={step === 1 || isSubmitting}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          {step < totalSteps && (
            <Button
              onClick={nextStep}
              disabled={isSubmitting}
              className="bg-blue-900 hover:bg-blue-800"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CatalystWizard;
